import {
  Avatar,
  Button,
  Container,
  Divider,
  Grid2,
  IconButton,
  Link,
  Stack,
} from "@mui/material";
import { ProfileViewModel } from "./ProfileViewModel";
import { HeaderView } from "../../component/HeaderView";
import { useTranslation } from "react-i18next";
import { CustomCardView } from "../../component/CustomCardView";
import { CustomTextView } from "../../component/CustomTextView";
import {
  COLOR_BLACK,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../../data/colors/Colors";
import { CustomTextField } from "../../component/CustomTextField";
import { useEffect } from "react";
import { CustomButtonView } from "../../component/CustomButtonView";
import useWindowDimensions from "../../../core/utils/UseWindowDimensions";
import {
  provideGetPlayerCurrentReferralUnlockedCodesUseCase,
  provideUpdatePlayerUseCase,
  provideUploadFileUseCase,
} from "../../di/UseCasesModule";
import { FilePicker } from "../../component/FilePicker";
import { Player } from "../../../domain/model/User";
import { CategoryUnlockCode } from "../../../domain/model/Category";
import { getReferralLinkFromUnlockCode } from "../../../core/utils/Utils";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import { ContentCopyRounded } from "@mui/icons-material";
import { copyToClipboard } from "../../../core/utils/UtilsInJS";
import { CustomImageView } from "../../component/CustomImageView";
import { LoadingView } from "../../component/LoadingView";
import { EmptyView } from "../../component/EmptyView";

export function ProfileView({
  playerModel,
}: {
  playerModel: Player | undefined;
}) {
  const viewModel = ProfileViewModel(
    provideUploadFileUseCase(),
    provideUpdatePlayerUseCase(),
    provideGetPlayerCurrentReferralUnlockedCodesUseCase()
  );

  const { t } = useTranslation();

  const { width } = useWindowDimensions();

  const player = viewModel.player;

  useEffect(() => {
    viewModel.setPlayer(playerModel);
  }, [playerModel]);

  return (
    <Stack>
      <HeaderView
        title={t("profile")}
        isLayoutFullWidthInSmallerScreens={false}
        layout={
          <Stack
            direction={"row"}
            alignItems={"center"}
            width={"100%"}
            justifyContent={"end"}
          >
            <CustomButtonView
              text={t("apply_changes")}
              props={{ paddingLeft: 2, paddingRight: 2 }}
              disabled={!player}
              onClick={viewModel.applyChanges}
            />
          </Stack>
        }
      />

      <Container maxWidth={false}>
        <Grid2 container spacing={2} pb={3} pt={3}>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
            <CustomCardView
              children={
                <Stack spacing={3} alignItems={"center"}>
                  <CustomTextView
                    text={t("profile_picture")}
                    fontWeight="bold"
                    variant="h5"
                  />
                  <Avatar
                    sx={{ width: 200, height: 200 }}
                    src={
                      viewModel.profileImageFile
                        ? URL.createObjectURL(viewModel.profileImageFile)
                        : player?.profileImage
                    }
                  />
                  <Button
                    sx={{
                      borderRadius: 22,
                      borderColor: COLOR_BLACK,
                      "&:hover": {
                        borderColor: COLOR_BLACK,
                      },
                    }}
                    variant="outlined"
                    fullWidth
                    disabled={!player}
                  >
                    <FilePicker
                      acceptType="image/*"
                      handleFilePicked={(file) => {
                        if (!file) return;
                        viewModel.setProfileImageFile(file);
                      }}
                      layout={
                        <CustomTextView
                          text={t("edit_picture")}
                          props={{ textTransform: "none" }}
                          textColor={!player ? COLOR_LIGHT_GRAY : COLOR_BLACK}
                        />
                      }
                    />
                  </Button>
                </Stack>
              }
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 8 }}>
            <CustomCardView
              children={
                <Stack spacing={3}>
                  <CustomTextView
                    text={t("edit_your_personal_info")}
                    fontWeight="bold"
                    variant="h5"
                  />

                  <Stack
                    gap={3}
                    direction={width < 700 ? "column" : "row"}
                    width={"100%"}
                  >
                    <Stack gap={1} width={"100%"}>
                      <CustomTextView
                        text={t("email")}
                        variant="body1"
                        textColor={COLOR_LIGHT_GRAY}
                      />
                      <CustomTextField
                        placeholder={t("email")}
                        onTextChange={() => {}}
                        disabled
                        value={player ? player.user?.email : t("loading")}
                      />
                    </Stack>
                    {player?.phoneNumber && (
                      <Stack gap={1} width={"100%"}>
                        <CustomTextView
                          text={t("phone_number")}
                          variant="body1"
                          textColor={COLOR_LIGHT_GRAY}
                        />
                        <CustomTextField
                          placeholder={t("phone_number")}
                          onTextChange={() => {}}
                          value={
                            player
                              ? `${player.phoneNumber?.countryCode}${player.phoneNumber?.number}`
                              : t("loading")
                          }
                          disabled
                          sxProps={{ direction: "ltr" }}
                        />
                      </Stack>
                    )}
                  </Stack>

                  <Stack spacing={1}>
                    <CustomTextView
                      text={t("name")}
                      fontWeight="bold"
                      variant="body1"
                    />
                    <CustomTextField
                      placeholder={t("name")}
                      onTextChange={(name) => {
                        viewModel.setPlayer({ ...player, name });
                      }}
                      maxLength={30}
                      disabled={!player}
                      value={player ? player.name : t("loading")}
                    />
                  </Stack>

                  <Stack spacing={1}>
                    <CustomTextView
                      text={t("bio")}
                      fontWeight="bold"
                      variant="body1"
                    />
                    <CustomTextField
                      placeholder={t("bio")}
                      value={player ? player.bio ?? "" : t("loading")}
                      onTextChange={(bio) => {
                        viewModel.setPlayer({ ...player, bio });
                      }}
                      disabled={!player}
                      maxLength={200}
                      multiLine
                      height={120}
                      borderRadius={6}
                    />
                  </Stack>
                </Stack>
              }
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <CustomCardView
              children={
                <Stack width={"100%"} gap={2}>
                  <CustomTextView
                    text={t("your_referral_codes")}
                    fontWeight="bold"
                    variant="h5"
                  />

                  {!viewModel.referralCodesLoading ? (
                    <>
                      {viewModel.referralCodes.length > 0 ? (
                        <Grid2 container spacing={2}>
                          {viewModel.referralCodes?.map((unlockCode, index) => {
                            return (
                              <Grid2
                                key={unlockCode._id}
                                size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}
                              >
                                <ReferralUnlockCodeView
                                  unlockCode={unlockCode}
                                  index={index}
                                />
                              </Grid2>
                            );
                          })}
                        </Grid2>
                      ) : (
                        <EmptyView minHeight={"10vh"} />
                      )}
                    </>
                  ) : (
                    <LoadingView minHeight="10vh" color={COLOR_MAIN} />
                  )}
                </Stack>
              }
            />
          </Grid2>
        </Grid2>
      </Container>
    </Stack>
  );
}

function ReferralUnlockCodeView({
  unlockCode,
  index,
}: {
  unlockCode: CategoryUnlockCode;
  index: number;
}) {
  const { t } = useTranslation();
  const url = getReferralLinkFromUnlockCode(unlockCode);
  return (
    <CustomCardView
      padding={2}
      background={COLOR_WHITE}
      children={
        <Stack width={"100%"} gap={2}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            width={"100%"}
            justifyContent={"space-between"}
          >
            <CustomTextView
              text={`#${index + 1}`}
              fontWeight="bold"
              variant="h5"
            />
            {unlockCode.usedBy && (
              <CustomTextView
                text={t("USED")}
                fontSize={12}
                props={{
                  bgcolor: "rgba(0,0,0,0.3)",
                  borderRadius: 22,
                  pl: 2,
                  pr: 2,
                  pt: 0.5,
                  pb: 0.5,
                }}
              />
            )}
          </Stack>
          <Stack>
            <Stack direction={"row"} gap={1} alignItems={"center"}>
              <CustomTextView
                text={t("URL")}
                fontWeight="bold"
                variant="body1"
              />

              <IconButton
                sx={{ border: "solid 1px black" }}
                onClick={async () => {
                  try {
                    await copyToClipboard(url);
                  } catch (e) {
                    alert(e);
                  }
                }}
              >
                <ContentCopyRounded
                  sx={{ color: COLOR_LESS_BLACK }}
                  fontSize="small"
                />
              </IconButton>
            </Stack>
            <CustomTextView text={url} />
          </Stack>

          <Stack>
            <Stack direction={"row"} gap={1} alignItems={"center"}>
              <CustomTextView
                text={t("Code")}
                fontWeight="bold"
                variant="body1"
              />
              <IconButton
                sx={{ border: "solid 1px black" }}
                onClick={async () => {
                  try {
                    await copyToClipboard(unlockCode.code);
                  } catch (e) {
                    alert(e);
                  }
                }}
              >
                <ContentCopyRounded
                  sx={{ color: COLOR_LESS_BLACK }}
                  fontSize="small"
                />
              </IconButton>
            </Stack>
            <CustomTextView text={unlockCode.code} />
          </Stack>

          <Stack gap={1}>
            <CustomTextView
              text={t("category")}
              fontWeight="bold"
              variant="body1"
            />
            <Stack direction={"row"} gap={1} alignItems={"center"}>
              <CustomTextView
                text={unlockCode.category?.title}
                textColor={COLOR_LIGHT_GRAY}
                variant="body1"
              />
              <CustomImageView
                src={unlockCode.category?.imageUrl}
                width={80}
                height={40}
                scale="contain"
                props={{ borderRadius: 2, background: COLOR_VERY_LIGHT_GRAY }}
              />
            </Stack>
          </Stack>

          {unlockCode.usedBy && (
            <>
              <Divider />
              <Stack gap={1}>
                <CustomTextView
                  text={t("used_by")}
                  variant="body1"
                  fontWeight="bold"
                />
                <Stack direction={"row"} alignItems={"center"} gap={1}>
                  <Avatar
                    src={unlockCode.usedBy?.profileImage}
                    sx={{ width: 60, height: 60 }}
                  />
                  <Stack>
                    <CustomTextView
                      text={unlockCode.usedBy?.name}
                      fontSize={16}
                      fontWeight="bold"
                    />
                    <CustomTextView
                      text={unlockCode.usedBy?.user?.email}
                      fontSize={16}
                      textColor={COLOR_LIGHT_GRAY}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </>
          )}
        </Stack>
      }
    />
  );
}
